import { useEffect, useRef, useState } from 'react';



import { Purchase as PurchaseRecord } from '@domain';
import { faArrowRight, faPuzzle, faSackDollar, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { alpha, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { numToCurrency, numToPercentage } from '@utils/stringHelpers';


type Props = {
	purchase: PurchaseRecord
	currency: string
	// onCompare: () => void
}

export function PurchaseCard({ purchase, currency }: Props) {
	const [isOverflowing, setIsOverflowing] = useState(false)

	const hasMatch = !!purchase.matchedProduct
	const isUnstocked = hasMatch && !purchase.matchedProduct.stocked

	// Handles overflow tooltip for purchase product description
	const descriptionRef = useRef<HTMLSpanElement>(null)

	useEffect(() => {
		const el = descriptionRef.current
		if (!el) return

		const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth)
		check()

		const observer = new ResizeObserver(check)
		observer.observe(el)
		return () => observer.disconnect()
	}, [purchase.description])

	return (
		<Paper
			elevation={4}
			data-testid={`direct-deal-purchase-${purchase.id}`}
			sx={{
				width: '100%',
				flexShrink: 0,
				borderRadius: 2,
				border: '1px solid',
				borderColor: isUnstocked ? 'warning.main' : 'divider',
				bgcolor: isUnstocked
					? (theme) => alpha(theme.palette.warning.main, 0.08)
					: 'background.paper',
				overflow: 'hidden',
				px: 2,
			}}
		>
			<Stack direction="row" alignItems="stretch" spacing={2}>
				{/* Purchase product */}
				<Stack justifyContent="center" sx={{ width: 300, py: 2 }} spacing={0.5}>
					<Tooltip title={isOverflowing ? purchase.description : ''}>
						<Typography ref={descriptionRef} variant="body2" noWrap>
							{purchase.description}
						</Typography>
					</Tooltip>
					<Stack direction="row" spacing={2}>
						<Typography variant="caption">QTY: {purchase.qtyPurchased}</Typography>
						<Typography variant="caption">
							Vol: {numToCurrency(purchase.caseVolume ?? 0, currency, 0)}
						</Typography>
					</Stack>
				</Stack>

				{/* Icon */}
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						width: 40,
						alignSelf: 'stretch',
						borderRadius: 1,
						bgcolor: (theme) => theme.palette.grey[50],
						borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
						borderRight: (theme) => `2px solid ${theme.palette.divider}`,
					}}
				>
					<FontAwesomeIcon icon={hasMatch ? faArrowRight : faXmark} size="lg" />
				</Stack>

				{/* Matched product (right side) */}
				<MatchedProduct purchase={purchase} currency={currency} />

				{/* fixme I hid this until the grouping is fixed, but we may not add it back in. */}
				{/* Compare button */}
				{/* <Button */}
				{/* 	variant="contained" */}
				{/* 	color="secondary" */}
				{/* 	size="small" */}
				{/* 	onClick={onCompare} */}
				{/* 	data-testid={`direct-deal-purchase-compare-${purchase.id}`} */}
				{/* 	sx={{ p: 1, whiteSpace: 'nowrap', alignSelf: 'center', mr: 2, flexShrink: 0 }} */}
				{/* > */}
				{/* 	Compare */}
				{/* </Button> */}
			</Stack>
		</Paper>
	)
}

type MatchedProductProps = {
	purchase: PurchaseRecord
	currency: string
}

function MatchedProduct({ purchase, currency }: MatchedProductProps) {
	const theme = useTheme()
	const { matchedProduct } = purchase

	if (!matchedProduct) {
		return (
			<Stack justifyContent="center" sx={{ flex: 2, py: 2, minWidth: 0 }}>
				<Typography variant="body2">No matched product</Typography>
			</Stack>
		)
	}

	const isExact = purchase.type === 'EXACT'

	return (
		<Stack height="100%" sx={{ flex: 2, py: 2, minWidth: 0, overflow: 'hidden' }} spacing={0.5}>
			<Stack direction="row" alignItems="center" spacing={1}>
				<FontAwesomeIcon
					icon={isExact ? faPuzzle : faSackDollar}
					size="lg"
					color={theme.palette.primary.main}
				/>
				<Typography variant="body2" noWrap>
					{matchedProduct.description}
				</Typography>
			</Stack>
			<Stack direction="row" spacing={2}>
				<Typography variant="caption" noWrap>
					Stocked:{' '}
					<Typography
						component="span"
						variant="caption"
						fontWeight="bold"
						color={matchedProduct.stocked ? 'inherit' : 'error'}
					>
						{matchedProduct.stocked ? 'Yes' : 'No'}
					</Typography>
				</Typography>
				<Tooltip title={numToCurrency(matchedProduct.annualPriceSavings ?? 0, currency, 5)}>
					<Typography variant="caption" noWrap>
						Price Savings:{' '}
						<Typography
							component="span"
							variant="caption"
							fontWeight="bold"
							color={(matchedProduct.annualPriceSavings ?? 0) < 0 ? 'error' : 'inherit'}
						>
							{numToCurrency(matchedProduct.annualPriceSavings ?? 0, currency, 0)}/yr
						</Typography>
					</Typography>
				</Tooltip>
				<Tooltip title={numToCurrency(matchedProduct.annualRebate ?? 0, currency, 5)}>
					<Typography variant="caption" noWrap>
						Rebate Savings:{' '}
						<Typography
							component="span"
							variant="caption"
							fontWeight="bold"
							color={(matchedProduct.annualRebate ?? 0) < 0 ? 'error' : 'inherit'}
						>
							{numToCurrency(matchedProduct.annualRebate ?? 0, currency, 0)}/yr
						</Typography>
					</Typography>
				</Tooltip>
				<Tooltip title={numToCurrency(matchedProduct.annualTotalSavings ?? 0, currency, 5)}>
					<Typography variant="caption" noWrap>
						Total Savings:{' '}
						<Typography
							component="span"
							variant="caption"
							fontWeight="bold"
							color={(matchedProduct.annualTotalSavings ?? 0) < 0 ? 'error' : 'inherit'}
						>
							{numToCurrency(matchedProduct.annualTotalSavings ?? 0, currency, 0)}/yr
						</Typography>
					</Typography>
				</Tooltip>
				<Typography variant="caption" noWrap>
					Savings:{' '}
					<Typography component="span" variant="caption" fontWeight="bold">
						{!matchedProduct.savingsPercentage
							? '--'
							: numToPercentage((matchedProduct.savingsPercentage ?? 0) / 100)}
					</Typography>
				</Typography>
			</Stack>
		</Stack>
	)
}
